import type { KyInstance } from 'ky'
import { isHTTPError } from 'ky'
import {
  paymentSystemIdByPlatform,
  waitForSubscriptionStatus,
  type SubscriptionServiceLevelStatus,
  type ServiceLevelStatus,
} from './subscribe'
import { tagFailure, type RestoreStep } from './failure'
import type { PaymentPlatform, Transaction, TransactionListing } from './types'

export type Purchase = {
  id: number
  created_at: string
  iap_sku: string
  purchase_type: 'subscription' | (string & {})
}

export class RestoreNoTransactionError extends Error {
  constructor() {
    super('No transactions found')
    this.name = 'RestoreNoTransactionError'
  }
}

export class RestoreError extends Error {
  public status?: number
  constructor(message: string, status?: number) {
    super(message)
    this.name = 'RestoreError'
    this.status = status
  }
}

export interface RestoreSubscriptionOptions {
  api: { service: KyInstance; receipt: KyInstance }
  userId: number
  platform: PaymentPlatform
  getAllUnclosedTransactions: () => Promise<TransactionListing>
  getAllEntitlements: () => Promise<TransactionListing>
  closeTransaction: (transaction: { transaction_id: string }) => Promise<void>
  currentSubscriptionExpiresAt?: string | null
}

export async function restoreSubscription(options: RestoreSubscriptionOptions) {
  const {
    api,
    userId,
    platform,
    getAllUnclosedTransactions,
    getAllEntitlements,
    closeTransaction,
    currentSubscriptionExpiresAt,
  } = options

  let storeReadFailed = false
  const subscriptionTransactions = (result: TransactionListing) => {
    if (!result.success) {
      storeReadFailed = true
      return []
    }
    return result.data.transactions.filter(
      (transaction) => transaction.product_type === 'subscription',
    )
  }
  const [transactions, entitlements] = await inStep(
    'restore_find_transaction',
    () =>
      Promise.all([
        getAllUnclosedTransactions().then(subscriptionTransactions),
        getAllEntitlements().then(subscriptionTransactions),
      ]),
  )

  let transaction: Transaction
  if (transactions.length > 0) {
    const [latestTransaction] = transactions.sort(
      (a, b) =>
        new Date(b.purchased_at).getTime() - new Date(a.purchased_at).getTime(),
    )
    transaction = latestTransaction
  } else if (entitlements.length > 0) {
    const [latestEntitlement] = entitlements.sort(
      (a, b) =>
        new Date(b.purchased_at).getTime() - new Date(a.purchased_at).getTime(),
    )
    transaction = latestEntitlement
  } else {
    // Nothing to restore, or a store read failed: the dialog is the same, but
    // support needs to know which.
    throw tagFailure(
      new RestoreNoTransactionError(),
      'restore_find_transaction',
      { cause: storeReadFailed ? 'native_failed' : 'none' },
    )
  }

  const { data: openedPurchases } = await inStep('restore_find_order', () =>
    api.service
      .get(`store/users/${userId}/purchase`, {
        searchParams: {
          status: 'open',
          page_size: 10,
          page: 1,
        },
      })
      .json<{ data: Purchase[] }>(),
  )

  const [openedPurchase] = openedPurchases
    .filter(
      (purchase) =>
        purchase.purchase_type ===
          (platform === 'china_android' ? 'iap' : 'subscription') &&
        purchase.iap_sku === transaction.sku,
    )
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )
  const purchaseId = openedPurchase?.id

  if (openedPurchase) {
    await inStep(
      'restore_mark_processing',
      () =>
        api.service.post(
          `store/users/${userId}/purchase/${openedPurchase.id}/mark-processing`,
        ),
      { purchaseId },
    )
  }

  let receiptRequestUrl = `projects/6/users/${userId}`
  let data = {}
  if (platform === 'china_android') {
    receiptRequestUrl += '/pending_iap_receipts'
    data = { receipt_data: transaction.transaction_id }
  } else {
    receiptRequestUrl += '/subscriptions'
    data = { transaction_id: transaction.transaction_id }
  }
  await inStep(
    'restore_post_transaction',
    () =>
      api.receipt.post(receiptRequestUrl, {
        json: {
          ...data,
          sku_id: transaction.sku,
          payment_system_id: paymentSystemIdByPlatform[platform],
        },
      }),
    { purchaseId },
  )

  // The receipt is already accepted, so a transaction that cannot be closed
  // does not fail the restore — as in subscribe's close step.
  try {
    await closeTransaction(transaction)
  } catch (error) {
    console.error('Failed to close restored transaction', error)
  }

  try {
    const serviceLevelStatus = await waitForSubscriptionStatus({
      serviceApi: api.service,
      userId,
      shouldResolve: (status): status is SubscriptionServiceLevelStatus => {
        return (
          status.service_level === 'subscription' &&
          status.subscription.expires_at !== currentSubscriptionExpiresAt
        )
      },
    })
    return serviceLevelStatus
  } catch {
    return null
  }
}

/** Runs a restore step, tagging what it throws with the step (ADR 0037). */
async function inStep<T>(
  step: RestoreStep,
  work: () => Promise<T>,
  details: { purchaseId?: number } = {},
): Promise<T> {
  try {
    return await work()
  } catch (error) {
    throw tagFailure(error, step, details)
  }
}
